import { parseXmlDate2 } from '../../../utility/date.helpers';
import {
    paramDays,
    paramDsName,
    paramDsNameHol,
    paramHours,
    paramMinutes,
    paramMonths,
    paramSeconds,
    paramWeekdays,
    paramWeeks,
    paramXPath,
    paramXpathHol,
    paramYears,
} from '../constants/parameterNames';
import {
    typeDataSource,
    typeNumber,
    typeXPath,
} from '../constants/parameterTypes';
import { failure } from '../helpers/commandResult';
import {
    getHolidayList,
    isHolidayDate,
    isWeekendDay,
} from '../helpers/holiday.helpers';

const optionalParameters = [
    {
        name: paramDsName,
        description: 'Data Source Name',
        type: typeDataSource,
    },
    {
        name: paramSeconds,
        description: '(+/-) Seconds to add',
        type: typeNumber,
    },
    {
        name: paramMinutes,
        description: '(+/-) Minutes to add',
        type: typeNumber,
    },
    {
        name: paramHours,
        description: '(+/-) Hours to add',
        type: typeNumber,
    },
    {
        name: paramDays,
        description: '(+/-) Days to add',
        type: typeNumber,
    },
    {
        name: paramWeekdays,
        description: '(+/-) Weekdays to add',
        type: typeNumber,
    },
    {
        name: paramWeeks,
        description: '(+/-) Weeks to add',
        type: typeNumber,
    },
    {
        name: paramMonths,
        description: '(+/-) Months to add',
        type: typeNumber,
    },
    {
        name: paramYears,
        description: '(+/-) Years to add',
        type: typeNumber,
    },
    {
        name: paramDsNameHol,
        description:
            'If using /weekdays parameter, can include a secondary data source for holidays to exclude.',
        type: typeDataSource,
    },
    {
        name: paramXpathHol,
        description:
            'If using /weekdays parameter, can include an xpath with holidays to exclude.',
        type: typeXPath,
        dsParentName: paramDsNameHol,
    },
];
const requiredParameters = [
    {
        name: paramXPath,
        description: 'XPath to starting Date or DateTime field.',
        type: typeXPath,
        dsParentName: paramDsName,
    },
];
const dateAddErrorFailedToSelectField =
    'Failed to select the field specified by the XPath.';
const dateAddErrorNothingToDo =
    'You must specify at least one part to add to the date.';
const dateAddErrorInvalidParamCombination =
    'If /xpathhol is provided, the weekdays parameter must be used.';
const dateAddErrorInvalidDate =
    'The field does not contain a valid Date or DateTime value.';
const dateAddErrorInvalidSeconds =
    'The value specified for Seconds is not a valid whole number value.';
const dateAddErrorInvalidMinutes =
    'The value specified for Minutes is not a valid whole number value.';
const dateAddErrorInvalidHours =
    'The value specified for Hours is not a valid whole number value.';
const dateAddErrorInvalidDays =
    'The value specified for Days is not a valid whole number value.';
const dateAddErrorInvalidWeekdays =
    'The value specified for Days is not a valid whole number value.';
const dateAddErrorInvalidWeeks =
    'The value specified for Weeks is not a valid whole number value.';
const dateAddErrorInvalidMonths =
    'The value specified for Months is not a valid whole number value.';
const dateAddErrorInvalidYears =
    'The value specified for Years is not a valid whole number value.';
const dateAddErrorInvalidXpath = 'Holidays node could not be selected.';
const dateAddErrorInvalidDatePart = 'Invalid date part.';

const description =
    'Adds to a date or datetime field. The resulting date will be returned as the Result value of the command.';

function getErrorMessage(datePart) {
    switch (datePart) {
        case 's':
            return dateAddErrorInvalidSeconds;

        case 'mi':
            return dateAddErrorInvalidMinutes;

        case 'hr':
            return dateAddErrorInvalidHours;

        case 'da':
            return dateAddErrorInvalidDays;

        case 'wd':
            return dateAddErrorInvalidWeekdays;

        case 'w':
            return dateAddErrorInvalidWeeks;

        case 'mo':
            return dateAddErrorInvalidMonths;

        case 'yr':
            return dateAddErrorInvalidYears;

        default:
            return dateAddErrorInvalidDatePart;
    }
}

function addWeekDays(cf, dt, weekDays, holidays) {
    var holidayList = getHolidayList(holidays),
        direction = weekDays < 0 ? -1 : 1;

    while (weekDays !== 0) {
        dt.setUTCDate(dt.getUTCDate() + direction);

        if (!isWeekendDay(dt) && !isHolidayDate(holidayList, dt)) {
            weekDays -= direction;
        }
    }
}

function addDatePart(cf, value, datePart, dt, holidays) {
    var intValue = cf.getIntegerValue(value);

    if (intValue === null || Number.isNaN(intValue)) {
        throw new Error(getErrorMessage(datePart));
    }

    switch (datePart) {
        case 's':
            dt.setUTCSeconds(dt.getUTCSeconds() + intValue);
            break;

        case 'mi':
            dt.setUTCMinutes(dt.getUTCMinutes() + intValue);
            break;

        case 'hr':
            dt.setUTCHours(dt.getUTCHours() + intValue);
            break;

        case 'da':
            dt.setUTCDate(dt.getUTCDate() + intValue);
            break;

        case 'wd':
            addWeekDays(cf, dt, intValue, holidays);
            break;

        case 'w':
            dt.setUTCDate(dt.getUTCDate() + intValue * 7);
            break;

        case 'mo':
            dt.setUTCMonth(dt.getUTCMonth() + intValue);
            break;

        case 'yr':
            dt.setUTCFullYear(dt.getUTCFullYear() + intValue);
            break;

        default:
            throw new Error(dateAddErrorInvalidDatePart);
    }
}

function DateAdd(params) {
    function executeAsync() {
        var cf = params.commonFunction,
            dsName = cf.getParamValue(paramDsName),
            xpath = cf.getParamValue(paramXPath),
            seconds = cf.getParamValue(paramSeconds),
            minutes = cf.getParamValue(paramMinutes),
            hours = cf.getParamValue(paramHours),
            days = cf.getParamValue(paramDays),
            weekdays = cf.getParamValue(paramWeekdays),
            weeks = cf.getParamValue(paramWeeks),
            months = cf.getParamValue(paramMonths),
            years = cf.getParamValue(paramYears),
            dsNameHol = cf.getParamValue(paramDsNameHol),
            xpathHol = cf.getParamValue(paramXpathHol),
            dom = cf.getDataSource(dsName),
            field = cf.getValidNode(
                dom,
                xpath,
                dateAddErrorFailedToSelectField,
            );

        // Make sure there is something to do.
        if (
            !seconds &&
            !minutes &&
            !hours &&
            !days &&
            !weekdays &&
            !weeks &&
            !months &&
            !years
        ) {
            return failure(dateAddErrorNothingToDo);
        }

        //if providing holidays, must use the weekdays component
        if (xpathHol && !weekdays) {
            return failure(dateAddErrorInvalidParamCombination);
        }
        var fieldValue = field.value(),
            dtObj = parseXmlDate2(fieldValue);

        if (!dtObj) {
            return failure(dateAddErrorInvalidDate);
        }

        var dt = dtObj.date;

        try {
            // Validate and process all parameters.
            if (seconds) {
                addDatePart(cf, seconds, 's', dt);
            }
            if (minutes) {
                addDatePart(cf, minutes, 'mi', dt);
            }
            if (hours) {
                addDatePart(cf, hours, 'hr', dt);
            }
            if (days) {
                addDatePart(cf, days, 'da', dt);
            }
            if (weekdays) {
                var holidays = null;

                if (xpathHol) {
                    holidays = cf.getValidNodeSet(
                        cf.getDataSource(dsNameHol),
                        xpathHol,
                        dateAddErrorInvalidXpath,
                    );
                }

                addDatePart(cf, weekdays, 'wd', dt, holidays);
            }
            if (weeks) {
                addDatePart(cf, weeks, 'w', dt);
            }
            if (months) {
                addDatePart(cf, months, 'mo', dt);
            }
            if (years) {
                addDatePart(cf, years, 'yr', dt);
            }
        } catch (e) {
            return failure(e.message);
        }

        //replace extra to get date in hh:mm:ss
        let dtString = dt.toISOString().replace('.000Z', '');

        if (fieldValue.indexOf('T') < 0) {
            dtString = dtString.substr(0, 10);
        }

        return dtString;
    }

    return {
        description,
        optionalParameters,
        requiredParameters,
        executeAsync,
    };
}

export default DateAdd;
